import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import { supabase } from '../lib/supabase';
import { fetchCruiseTimes, fetchBoardingPoint, fetchCruiseNames } from '../lib/cruiseDetails';
import { displayDeck, cruiseLabel } from '../components/shared/helpers';

// ─── Hardcoded ticket content (future: read from ticket_settings) ─────────────
export const TICKET_INSTRUCTIONS = [
  'Please arrive by your reporting time.',
  'No refunds for late arrivals or missed boarding.',
  'Verify boarding location before arrival.',
  'Outside food and beverages are strictly not permitted on board.',
  'Middle Deck: 1 flight of stairs. Upper Deck: 2 flights of stairs.',
  'Middle Deck ticket holders are not permitted on the Upper Deck.',
  'Table allocation is at the discretion of Alfresco Grand. Please cooperate.',
  'This ticket is non-transferable and non-refundable.',
  'Please maintain respectful conduct with all staff.',
  'Management reserves the right to deny boarding or remove any guest for misconduct, without refund.',
  'In case of rain, Upper Deck guests will be accommodated in the Middle Deck and the lounge. No refund will be provided for change in seating due to weather conditions.',
];

export const TICKET_TC = [
  'By boarding, you accept these terms in full.',
  'No cancellations or refunds once ticket is confirmed.',
  'Weather cancellations — Sunset/Sundown: full refund. Dinner Cruise: no refund.',
  'In case of rain, Upper Deck guests will be moved to Middle Deck/lounge. No refund.',
  'Itinerary subject to change based on weather and river conditions.',
  'Management is not liable for health-related issues on board.',
  'Pets and outside items strictly prohibited.',
  'Disputes subject to jurisdiction of courts in Guwahati, Assam.',
];

// Fallback information lines, used only when ticket_settings.instructions is
// empty. The boarding line carries a placeholder: the boarding point moves with
// the season (Panbazar in winter, Uzanbazar in summer), so it is filled in at
// render time from the boarding point in force on the cruise date. The live
// instructions text may use the same {boarding_point} placeholder, so staff
// never have to retype the ghat inside the wording when boarding moves.
export const BOARDING_POINT_PLACEHOLDER = '{boarding_point}';
export const TICKET_IMPORTANT_INFORMATION = [
  'Please arrive by your reporting time. The cruise will not wait for late arrivals and no refund will be provided.',
  `Verify boarding location before arrival: ${BOARDING_POINT_PLACEHOLDER}.`,
  'Outside food and beverages are strictly not permitted on board.',
  'Middle Deck: 1 flight of stairs. Upper Deck: 2 flights of stairs. Middle Deck ticket holders are not permitted on the Upper Deck.',
  'In case of rain, Upper Deck guests will be accommodated in the Middle Deck and lounge. No refund for change in seating due to weather.',
  'Table allocation is at the discretion of Alfresco Grand. Please cooperate with seating arrangements.',
  'This ticket is non-transferable and non-refundable. No cancellations once confirmed.',
  'Please maintain respectful conduct with all staff. Management reserves the right to deny boarding or remove any guest for misconduct without refund.',
  'Itinerary subject to change based on weather and river conditions.',
  'Management is not liable for health-related issues on board. Pets strictly prohibited.',
  'Disputes subject to jurisdiction of courts in Guwahati, Assam. Contact: info@bcpl.in',
];

/** Any lines with the {boarding_point} placeholder filled in with the live value. */
export function applyBoardingPoint(lines, boardingPoint) {
  return lines.map((line) =>
    line.split(BOARDING_POINT_PLACEHOLDER).join(boardingPoint || 'the boarding point printed on this ticket'),
  );
}

/** The fallback lines with the live boarding point filled in. */
export function fallbackInfoLines(boardingPoint) {
  return applyBoardingPoint(TICKET_IMPORTANT_INFORMATION, boardingPoint);
}

// ─── Dinner Cruise information lines ──────────────────────────────────────────
// A Dinner Cruise ticket's DECK box reads "Performance Deck", so the generic
// lines that name the Middle / Upper Deck (stairs, deck access, the rain move)
// no longer tell that guest which rule is theirs — and the rain line does not
// apply to dinner at all. The live lines come from ticket_settings.instructions
// (staff-edited, shared by every cruise), so rather than a second settings
// field we drop any line naming a deck and put the dinner lines where the first
// one stood. Sunset / Sundown tickets are returned untouched.
const DINNER_DECK_LINES = [
  'Your seating is on the Performance Deck, one flight of stairs up.',
  'The Upper Deck is not open to Dinner Cruise guests.',
];
const DECK_LINE_RE = /\b(middle|upper)\s+deck\b/i;

export function infoLinesFor(cruiseType, lines) {
  if (cruiseType !== 'Dinner Cruise') return lines;
  const out = [];
  let placed = false;
  for (const line of lines) {
    if (DECK_LINE_RE.test(line)) {
      if (!placed) { out.push(...DINNER_DECK_LINES); placed = true; }
      continue;
    }
    out.push(line);
  }
  if (!placed) out.push(...DINNER_DECK_LINES);
  return out;
}

// ─── Colors ───────────────────────────────────────────────────────────────────
const NAVY      = [10, 22, 40];     // #0A1628 — primary text + masthead
const GOLD      = [201, 168, 76];   // #C9A84C — masthead + report-by accent only
const GREY      = [136, 147, 160];  // #8A93A0 — all field labels
const GOLD_DARK = [169, 132, 47];   // #A9842F — report-by label
const TINT_GREY = [244, 246, 248];  // #F4F6F8 — cruise band tint
const TINT_GOLD = [251, 246, 232];  // #FBF6E8 — report-by cell tint
const BORDER    = [228, 231, 236];  // #E4E7EC — hairline borders

// ─── Helpers ─────────────────────────────────────────────────────────────────
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const ordinal = (n) => { const s = ['th','st','nd','rd']; const v = n % 100; return n + (s[(v-20)%10] || s[v] || s[0]); };
const fmtDate = (ddmmyyyy) => {
  if (!ddmmyyyy) return '—';
  const [dd, mm, yyyy] = ddmmyyyy.split('-');
  return `${ordinal(parseInt(dd, 10))} ${MONTHS[parseInt(mm, 10) - 1]} ${yyyy}`;
};
const to12hr = (hhmm) => {
  if (!hhmm) return '—';
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
};
// Guest breakdown — non-zero categories only, correct singular/plural, joined by " · "
const pluralize = (n, singular, plural) => `${n} ${n === 1 ? singular : plural}`;
const buildGuests = (booking) => {
  const parts = [];
  const a = Number(booking.adults       || 0);
  const c = Number(booking.children     || 0);
  const i = Number(booking.infants      || 0);
  const t = Number(booking.tour_leaders || 0);
  if (a > 0) parts.push(pluralize(a, 'Adult', 'Adults'));
  if (c > 0) parts.push(pluralize(c, 'Child', 'Children'));
  if (i > 0) parts.push(pluralize(i, 'Infant', 'Infants'));
  if (t > 0) parts.push(pluralize(t, 'Tour Leader', 'Tour Leaders'));
  return parts.join(' · ') || '—';
};

// ─── Logo loader — removes black background via canvas pixel processing ────────
export async function loadLogoDataUrl() {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width  = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const id = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const d  = id.data;
      // Make dark pixels transparent so white script shows on any background
      for (let i = 0; i < d.length; i += 4) {
        const lum = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
        d[i + 3] = lum < 128 ? 0 : d[i + 3];
      }
      ctx.putImageData(id, 0, 0);
      resolve({ dataUrl: canvas.toDataURL('image/png'), aspect: img.width / img.height });
    };
    img.onerror = () => resolve(null);
    img.src = '/alfresco-grand.png';
  });
}

// ─── Asset fetcher ─────────────────────────────────────────────────────────────
// Times and boarding point come from the shared cruiseDetails lookup for the
// booking's cruise DATE, so a reprint of an old ticket shows what applied on
// that sailing, not whatever is current.
async function fetchTicketAssets(booking) {
  const [refRes, cruiseTimes, boarding, settingsRes, cruiseNames] = await Promise.all([
    supabase.from('booking_references').select('reference_number').eq('booking_id', booking.id).single(),
    fetchCruiseTimes({ cruiseName: booking.cruise_type, date: booking.date }),
    fetchBoardingPoint({ date: booking.date }),
    supabase.from('ticket_settings').select('instructions').limit(1).single(),
    fetchCruiseNames({ cruiseName: booking.cruise_type }),
  ]);
  return {
    referenceNumber: refRes.data?.reference_number || null,
    cruiseSetting:   cruiseTimes || null,
    cruiseNames:     cruiseNames || null,
    boardingPoint:   boarding?.boarding_point || 'Guwahati',
    customInfo:      settingsRes.data?.instructions || null,
  };
}

// ─── Layout renderer (pure drawing; all data already resolved/formatted) ───────
function renderTicket(doc, d) {
  const W = 210, H = 297, M = 14, CW = W - 2 * M;

  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, W, H, 'F');

  // ── MASTHEAD — full-width navy band with gold rule along the bottom edge ────
  const MH = 32;
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, W, MH, 'F');
  doc.setFillColor(...GOLD);
  doc.rect(0, MH - 0.6, W, 0.6, 'F');

  if (d.logo?.dataUrl) {
    const lh = 13, lw = lh * d.logo.aspect;
    doc.addImage(d.logo.dataUrl, 'PNG', M, 9.5, lw, lh);
  } else {
    // Text fallback (logo carries the unit line as part of the image)
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text('ALFRESCO GRAND', M, 17);
    doc.setTextColor(...GOLD);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.text('A unit of Brahmaputra Cruise Pvt. Ltd.', M, 23.5);
  }

  // BOARDING PASS — gold, uppercase, letter-spaced, right-aligned.
  // (align:'right' ignores charSpace, so compute the spaced width and left-draw.)
  doc.setTextColor(...GOLD);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  const bpCS = 1.2, bpTxt = 'BOARDING PASS';
  doc.setCharSpace(bpCS);
  const bpW = doc.getTextWidth(bpTxt) + bpCS * bpTxt.length;
  doc.text(bpTxt, W - M - bpW, 18.5);
  doc.setCharSpace(0);

  // ── HERO — passenger + booking ref (left), scannable QR (right) ─────────────
  const heroTop = 52;
  const qrBox = 44, qrBoxX = W - M - qrBox, qrBoxY = 46;
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.3);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(qrBoxX, qrBoxY, qrBox, qrBox, 2, 2, 'FD');
  if (d.qrDataUrl) {
    const qr = 38, pad = (qrBox - qr) / 2;
    doc.addImage(d.qrDataUrl, 'PNG', qrBoxX + pad, qrBoxY + pad, qr, qr);
  }
  doc.setTextColor(...GREY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  doc.setCharSpace(0.5);
  doc.text('SCAN TO BOARD', qrBoxX + qrBox / 2, qrBoxY + qrBox + 5, { align: 'center' });
  doc.setCharSpace(0);

  const leftW = qrBoxX - M - 8;
  doc.setTextColor(...GREY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setCharSpace(0.4);
  doc.text('PASSENGER', M, heroTop);
  doc.setCharSpace(0);
  let nameFs = 24;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(nameFs);
  const nm = d.name || 'GUEST';
  while (nameFs > 13 && doc.getTextWidth(nm) > leftW) { nameFs -= 1; doc.setFontSize(nameFs); }
  doc.setTextColor(...NAVY);
  doc.text(nm, M, heroTop + 12);

  doc.setTextColor(...GREY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setCharSpace(0.4);
  doc.text('BOOKING REFERENCE', M, heroTop + 26);
  doc.setCharSpace(0);
  doc.setTextColor(...NAVY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.text(d.reference, M, heroTop + 34);

  // ── CRUISE BAND — serif cruise name (left), date (right) ────────────────────
  const cbY = 108, cbH = 18;
  doc.setFillColor(...TINT_GREY);
  doc.rect(0, cbY, W, cbH, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...GREY);
  doc.setCharSpace(0.4);
  doc.text('DATE', W - M, cbY + 7, { align: 'right' });
  doc.setCharSpace(0);
  doc.setTextColor(...NAVY);
  doc.setFontSize(12);
  doc.text(d.dateStr, W - M, cbY + 13.5, { align: 'right' });
  let cFs = 17;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(cFs);
  const cMax = CW - 52;
  while (cFs > 11 && doc.getTextWidth(d.cruiseName) > cMax) { cFs -= 0.5; doc.setFontSize(cFs); }
  doc.setTextColor(...NAVY);
  doc.text(d.cruiseName, M, cbY + cbH / 2 + 2.2);

  // ── TIME GRID — REPORT BY (gold accent), DEPARTURE, ENDS ────────────────────
  const tgY = 136, tGap = 4, tW = (CW - 2 * tGap) / 3, tH = 22;
  const times = [
    { label: 'REPORT BY', value: d.reportBy, accent: true },
    { label: 'DEPARTURE', value: d.departure },
    { label: 'ENDS',      value: d.ends },
  ];
  times.forEach((t, i) => {
    const x = M + i * (tW + tGap);
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.3);
    doc.setFillColor(...(t.accent ? TINT_GOLD : [255, 255, 255]));
    doc.roundedRect(x, tgY, tW, tH, 2, 2, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setCharSpace(0.4);
    doc.setTextColor(...(t.accent ? GOLD_DARK : GREY));
    doc.text(t.label, x + 5, tgY + 8);
    doc.setCharSpace(0);
    doc.setTextColor(...NAVY);
    doc.setFontSize(13);
    doc.text(t.value, x + 5, tgY + 16.5);
  });

  // ── DETAIL ROW — DECK, GUESTS ───────────────────────────────────────────────
  const drY = tgY + tH + 8, dGap = 4, dW = (CW - dGap) / 2, dH = 20;
  const details = [
    { label: 'DECK',   value: d.deck },
    { label: 'GUESTS', value: d.guestsStr },
  ];
  details.forEach((it, i) => {
    const x = M + i * (dW + dGap);
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.3);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(x, drY, dW, dH, 2, 2, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setCharSpace(0.4);
    doc.setTextColor(...GREY);
    doc.text(it.label, x + 5, drY + 8);
    doc.setCharSpace(0);
    doc.setTextColor(...NAVY);
    let vFs = 12.5;
    doc.setFontSize(vFs);
    while (vFs > 8 && doc.getTextWidth(it.value) > dW - 10) { vFs -= 0.5; doc.setFontSize(vFs); }
    doc.text(it.value, x + 5, drY + 15);
  });

  // ── BOARDING POINT — thin-bordered callout ──────────────────────────────────
  const bpY = drY + dH + 8, bpH = 18;
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.3);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(M, bpY, CW, bpH, 2, 2, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setCharSpace(0.4);
  doc.setTextColor(...GREY);
  doc.text('BOARDING POINT', M + 6, bpY + 7);
  doc.setCharSpace(0);
  doc.setTextColor(...NAVY);
  doc.setFontSize(11.5);
  doc.text(d.boardingPoint, M + 6, bpY + 13.5);

  // ── IMPORTANT INFORMATION — numbered, two columns, anchored near the bottom ─
  let iy = bpY + bpH + 11;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...GREY);
  doc.setCharSpace(0.6);
  doc.text('IMPORTANT INFORMATION', M, iy);
  doc.setCharSpace(0);
  iy += 6;

  const colGap = 10, colW = (CW - colGap) / 2;
  const col2X = M + colW + colGap;
  const dividerX = M + colW + colGap / 2;
  const half = Math.ceil(d.infoLines.length / 2);
  const col1 = d.infoLines.slice(0, half);
  const col2 = d.infoLines.slice(half);
  const lineH = 3.15, ptGap = 2.3, fontPt = 6.5;

  const drawCol = (items, x, startNum) => {
    let yy = iy;
    items.forEach((line, i) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(fontPt);
      doc.setTextColor(...GREY);
      const wrapped = doc.splitTextToSize(`${startNum + i}.  ${line}`, colW - 2);
      wrapped.forEach((tl, li) => doc.text(tl, x, yy + li * lineH));
      yy += wrapped.length * lineH + ptGap;
    });
    return yy;
  };
  const end1 = drawCol(col1, M, 1);
  const end2 = drawCol(col2, col2X, half + 1);

  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.3);
  doc.line(dividerX, iy - 2.5, dividerX, Math.max(end1, end2) - ptGap + 0.5);
}

// ─── Shared doc builder ──────────────────────────────────────────────────────
// Resolves all assets and renders the EXACT same ticket used for download. Both
// downloadTicket (save) and getTicketPdfBase64 (WhatsApp upload) go through this
// single path, so the WhatsApp ticket is byte-for-byte the downloaded one.
async function buildTicketDoc(booking, preloadedReference) {
  const [assets, logo] = await Promise.all([
    fetchTicketAssets(booking),
    loadLogoDataUrl(),
  ]);

  const { referenceNumber: fetchedRef, cruiseSetting, cruiseNames, boardingPoint, customInfo } = assets;
  const infoLines = infoLinesFor(
    booking.cruise_type,
    customInfo
      ? applyBoardingPoint(customInfo.split('\n').filter(s => s.trim()), boardingPoint)
      : fallbackInfoLines(boardingPoint),
  );

  // This public site ALWAYS passes the real reference (from the booking write)
  // as preloadedReference; fetchedRef is a belt-and-braces fallback, and the
  // AG- form a last resort if neither resolves.
  let reference = preloadedReference || fetchedRef;
  if (!reference) reference = `AG-${booking.id.slice(0, 8).toUpperCase()}`;

  let qrDataUrl = null;
  try {
    qrDataUrl = await QRCode.toDataURL(reference, {
      width: 260, margin: 1,
      color: { dark: '#0A1628', light: '#FFFFFF' },
    });
  } catch (err) {
    console.error('QR generation failed:', err);
  }

  // ── Render ───────────────────────────────────────────────────────────────
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  renderTicket(doc, {
    name:         (booking.booking_name || 'GUEST').toUpperCase(),
    reference,
    // Guest-facing long name (display only); the stored cruise_type still
    // drives the deck label and the dinner information lines above.
    cruiseName:   cruiseLabel(cruiseNames) || booking.cruise_type || '—',
    deck:         displayDeck(booking.cruise_type, booking.deck) || '—',
    dateStr:      fmtDate(booking.date),
    reportBy:     to12hr(cruiseSetting?.reporting_time),
    departure:    to12hr(cruiseSetting?.departure_time),
    ends:         to12hr(cruiseSetting?.end_time),
    guestsStr:    buildGuests(booking),
    boardingPoint,
    infoLines,
    qrDataUrl,
    logo,
  });

  const safeName = (booking.booking_name || 'Guest')
    .trim()
    .replace(/[^a-zA-Z0-9 ]/g, '')
    .trim()
    .replace(/\s+/g, '-');
  const filename = `Alfresco-${reference}-${safeName}-${booking.date}.pdf`;

  return { doc, reference, filename };
}

// ─── PDF generator — A4 portrait (download; unchanged behaviour) ──────────────
export async function downloadTicket(booking, preloadedReference) {
  const { doc, filename } = await buildTicketDoc(booking, preloadedReference);
  doc.save(filename);
}

// ─── Same ticket as base64 — for the WhatsApp send flow (no download) ─────────
export async function getTicketPdfBase64(booking, preloadedReference) {
  const { doc, filename } = await buildTicketDoc(booking, preloadedReference);
  const dataUri = doc.output('datauristring'); // "data:application/pdf;...;base64,XXXX"
  const base64 = dataUri.substring(dataUri.indexOf(',') + 1);
  return { base64, filename };
}
